import * as yup from 'yup';
import { AccessControlServiceEntity } from 'constants-integrations';

import { validateInputForKastle } from './kastleNameRules';

/**
 * Rules that a building's visitor integrations (from the IGS integrations
 * manifest for the VISITORS domain) place on visitor details, beyond our own
 * validators. Forms, bulk importers and the server all go through
 * `validateVisitorInput`, so they agree on what is rejected.
 */

export type Translator = (key: string, params?: any) => string;

export type VisitorProvider = AccessControlServiceEntity;

export type VisitorField = 'firstName' | 'lastName' | 'email' | 'phone' | 'company';

export type VisitorInput = Partial<Record<VisitorField, string | null | undefined>>;

/** Why a provider rejects a field. Everything except `message` is safe to log. */
export type VisitorFieldError = {
  field: VisitorField;
  /** Provider-specific reason, e.g. Kastle: 'hidden' | 'fullWidth' | 'other'. */
  kind: string;
  /** Translated, user-facing reason; may quote the rejected character. */
  message: string;
  /** The first character the provider refuses, when the rule is about characters. */
  character?: string;
};

export type VisitorInputValidator = (
  input: VisitorInput,
  translator: Translator
) => VisitorFieldError[];

/** The only place that maps a provider to its visitor rules. To support a provider, add it here. */
const validateInputByIntegration: Readonly<
  Partial<Record<VisitorProvider, VisitorInputValidator>>
> = {
  [AccessControlServiceEntity.Kastle]: validateInputForKastle,
};

const KNOWN_PROVIDERS = new Set<string>(Object.values(AccessControlServiceEntity));

function isVisitorProvider(provider: string): provider is VisitorProvider {
  return KNOWN_PROVIDERS.has(provider);
}

/** Shared empty list, so callers that default to it keep a stable identity between renders. */
export const NO_VISITOR_PROVIDERS: ReadonlyArray<VisitorProvider> = Object.freeze([]);

/**
 * The providers listed in a building's IGS integrations manifest. Unknown
 * values are dropped, so IGS can add a provider without breaking lane-next.
 */
export function getVisitorProviders(
  integrations: ReadonlyArray<{ provider: string }> | null | undefined
): ReadonlyArray<VisitorProvider> {
  const providers = new Set(
    (integrations ?? [])
      .map(integration => integration.provider)
      .filter(isVisitorProvider)
  );

  return providers.size ? [...providers] : NO_VISITOR_PROVIDERS;
}

/** Every rule the given providers break for this input. Providers without rules add nothing. */
export function validateVisitorInput(
  input: VisitorInput,
  providers: ReadonlyArray<VisitorProvider>,
  translator: Translator
): Array<VisitorFieldError & { provider: VisitorProvider }> {
  return providers.flatMap(provider =>
    (validateInputByIntegration[provider]?.(input, translator) ?? []).map(
      error => ({ provider, ...error })
    )
  );
}

/** Adds the providers' rules for one field to an existing yup validator for that field. */
export function withIntegrationValidation(
  schema: yup.StringSchema,
  field: VisitorField,
  providers: ReadonlyArray<VisitorProvider>,
  translator: Translator
): yup.StringSchema {
  if (providers.length === 0) {
    return schema;
  }

  return schema.test(
    'visitor-integration-rules',
    '',
    function visitorIntegrationRules(value?: string | null) {
      const error = validateVisitorInput(
        { [field]: value },
        providers,
        translator
      ).find(fieldError => fieldError.field === field);

      return error ? this.createError({ message: error.message }) : true;
    }
  );
}
